<template>
    <persistent-banner
        v-show="canHaveGlobalBanner && showBanners"
        id="global-banner"
        ref="globalBanner"
        class="global-banner"
        background-color="var(--palette-gray-100)">
        <component
            :is="componentType"
            :mau-limit="mauLimit"
            :plan="plan"
            :home-subscription="homeSubscription"
            :is-eligible-for-trial="isEligibleForTrial"
            :hard-paywall-enabled="enforceMauLimiting"
            :days-remaining-in-trial="daysRemainingInTrial"
            :is-in-auto-provisioned-trial="isInAutoProvisionedTrial"
            :can-access-starter-upgrade="canAccessStarterUpgrade"
            :has-installed="hasInstalled"
            @start-trial="startTrial"
            @primary-action="primaryAction"
            @secondary-action="secondaryAction"
            @close="close" />
    </persistent-banner>
</template>

<script>
import { mapGetters, mapState, useStore } from 'vuex';
import { PendoProgressCircle } from '@pendo/components';
import { EventBus } from '@utils/event-bus.js';
import { warningLimitPercentage } from '@/free/free.constants.js';
import GLOBAL_BANNER_TYPES from '@/constants/global-banner-types';
import { openContactSalesForm } from '@/free/contact-sales-form/contact-sales-form.vue';
import BannerAlert from '@/common/banner-notification/banner-alert/banner-alert.vue';
import PersistentBanner from '@/common/persistent-banner/persistent-banner.vue';
import OverMau from './global-banner-types/over-mau.vue';
import MauWarning from './global-banner-types/mau-warning.vue';
import Trial from './global-banner-types/trial.vue';
import SupportAccessWarning from './global-banner-types/support-access-warning.vue';
import { activePlan, canHaveGlobalBanner } from '@/global-banner/global-banner.utils';
import { isOnGlobalSettingsPage, isOnReplayPlayerPage } from '@/vue-router/route.utils';
import { useElementSize } from '@vueuse/core';
import { useTemplateRef, watch, onMounted } from 'vue';

export default {
    name: 'GlobalBanner',
    components: {
        BannerAlert,
        PendoProgressCircle,
        PersistentBanner,
        OverMau,
        MauWarning,
        Trial,
        SupportAccessWarning,
    },
    props: {
        isImpersonating: {
            type: Boolean,
            default: false,
        },
        homeSubscription: {
            type: Object,
            required: true,
        },
    },
    setup () {
        const store = useStore();
        const globalBanner = useTemplateRef('globalBanner');

        onMounted(() => {
            const { height: globalBannerHeight } = useElementSize(globalBanner);

            watch(globalBannerHeight, (topBannerHeight) => {
                store.commit('leftNav/setTopBannerHeight', { topBannerHeight });
            });
        });

        return {
            globalBanner,
        };
    },
    computed: {
        ...mapState({
            isTopBannerDismissed: (state) => state.leftNav.isTopBannerDismissed,
        }),
        ...mapGetters({
            hasSegmentFlag: 'auth/hasSegmentFlag',
            canUpgrade: 'subscription/canUpgrade',
            getFeatureFlag: 'subscription/getFeatureFlag',
            hasInstalled: 'subscription/snippetInstalled',
            isEligibleForTrial: 'subscription/isEligibleForTrial',
            daysRemainingInTrial: 'subscription/daysRemainingInTrial',
            isInAutoProvisionedTrial: 'subscription/isInAutoProvisionedTrial',
            mauLimit: 'subscription/mauLimit',
            mauCount: 'subscription/mauCount',
            isOverMauLimit: 'subscription/isOverMauLimit',
            isInTrial: 'subscription/isInTrial',
        }),
        componentType () {
            if (this.type === GLOBAL_BANNER_TYPES.MAU_WARNING) {
                return MauWarning;
            }
            if (this.type === GLOBAL_BANNER_TYPES.OVER_MAU) {
                return OverMau;
            }
            if (this.type === GLOBAL_BANNER_TYPES.TRIAL) {
                return Trial;
            }
            if (this.type === GLOBAL_BANNER_TYPES.SUPPORT_ACCESS_WARNING) {
                return SupportAccessWarning;
            }

            return '';
        },
        canAccessStarterUpgrade () {
            return (
                (this.plan === 'starterPackage' || this.plan === 'starter2024Package') &&
                this.hasSegmentFlag('starterUpgradePage')
            );
        },
        enforceMauLimiting () {
            return this.getFeatureFlag('enforceMauLimiting');
        },
        plan () {
            return activePlan();
        },
        type () {
            if (this.isImpersonating && !this.$activeUser?.isGuest && !this.autoGrantAccessEnabled) {
                return GLOBAL_BANNER_TYPES.SUPPORT_ACCESS_WARNING;
            }
            if (['BuyNow', 'TrialExpired'].includes(this.$route?.name)) {
                return GLOBAL_BANNER_TYPES.NONE;
            }
            if (this.isInTrial || this.isInAutoProvisionedTrial) {
                return GLOBAL_BANNER_TYPES.TRIAL;
            }
            if (this.enforceMauLimiting && this.isOverMauLimit && this.mauLimit > 0) {
                return GLOBAL_BANNER_TYPES.OVER_MAU;
            }
            if (!this.enforceMauLimiting && this.mauCount >= this.mauLimit && this.mauLimit > 0) {
                return GLOBAL_BANNER_TYPES.OVER_MAU;
            }
            if (this.mauCount >= this.mauLimit * warningLimitPercentage && this.mauLimit > 0) {
                return GLOBAL_BANNER_TYPES.MAU_WARNING;
            }

            return 'none';
        },
        showBanners () {
            return (
                !this.isOnGlobalSettings &&
                !this.isOnOauthDetailsPage &&
                !this.isOnReplayPlayerPage &&
                !this.isOnSetupPage &&
                !!this.componentType
            );
        },
        canHaveGlobalBanner () {
            const params = {
                isTopBannerDismissed: this.isTopBannerDismissed,
                currentRoute: this.$route?.name,
            };

            return canHaveGlobalBanner(params);
        },
        autoGrantAccessEnabled () {
            return !!(this.$activeSubscription?.autoGrantAccessExpiresOn && this.$activeSubscription.autoGrantAccessExpiresOn > Date.now());
        },
        isOnGlobalSettings () {
            return isOnGlobalSettingsPage(this.$route);
        },
        isOnReplayPlayerPage () {
            return isOnReplayPlayerPage(this.$route?.path);
        },
        isOnOauthDetailsPage () {
            return this.$route?.path === '/oauth/authorize';
        },
        isOnSetupPage () {
            return this.$route?.name === 'Setup';
        },
    },
    methods: {
        close () {
            // account for fadeout animation
            window.setTimeout(() => {
                EventBus.$emit('closeTopBanner');
            }, 250);
        },
        primaryAction () {
            const url = window.location.href;
            if (this.canUpgrade) {
                return this.$router.push({ name: 'BuyNow' });
            }

            if (this.canAccessStarterUpgrade) {
                return this.$router.push({ name: 'UpgradeNow' });
            }

            if (url.includes('data-management')) {
                window.pendo.track('Data Sync Page Trial Banner Contact Sales');

                return this.openContactSalesForm('data-sync');
            }

            return this.openContactSalesForm();
        },
        secondaryAction () {
            if (!this.hasInstalled) {
                return this.$router.push({ name: 'OnboardingHomepage' });
            }
        },
        startTrial () {
            EventBus.$emit('openFreeTrialForm');
        },
        openContactSalesForm,
    },
};
</script>

<style scoped lang="less">
.global-banner {
    :deep(.persistent-banner) {
        padding: 0;
        min-height: 0;
        border-bottom: 1px solid var(--palette-gray-40);

        .persistent-banner__content {
            padding: 0;
            font-weight: inherit;
            display: block;
            max-height: fit-content;
        }
    }
}

.pendo-banner {
    .pendo-alert {
        border: none;
        border-radius: 0;
    }

    .is-bold {
        font-weight: bold;
    }

    .pendo-progress-circle {
        margin-right: 16px;
    }
}
</style>
